import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { snack } from '@innovamat/glimmer-components';
import type { GlowApiError } from '@innovamat/glow-api-client';
import { useCopyToClipboard } from '@innovamat/hooks';
import { getSchoolTeachersQueryKey } from '../../../../hooks/use-school-teachers';

import { useUser } from '../../../../../user-management';
import type { CreateItem } from '../../../../components/common-table-components/actions-dropdown';
import { ActionsDropdown } from '../../../../components/common-table-components/actions-dropdown';
import { useOrganization } from '../../../../hooks/use-organization';
import { useResendInvitation } from '../../../../hooks/use-resend-invitation';
import { useSchoolCapabilities } from '../../../../hooks/use-school-capabilities';
import type { OrganizationTeacher } from '../../../../modules/teacher';

type ActionsCellProps = {
  isDeleteOptionDisabled: boolean;
  isPlatformEnabled: boolean;
  isUpdatePeriod: boolean;
  organizationId: string;
  teacher: OrganizationTeacher;
  onDeleteTeacher: (teacher: OrganizationTeacher) => void;
  onEditClassrooms: (teacher: OrganizationTeacher) => void;
};

function ActionsCell({
  isDeleteOptionDisabled,
  isPlatformEnabled,
  isUpdatePeriod,
  organizationId,
  teacher,
  onDeleteTeacher,
  onEditClassrooms,
}: ActionsCellProps): JSX.Element | null {
  const { t } = useTranslation();
  const { isRole } = useUser();
  const [_, copy] = useCopyToClipboard();
  const queryClient = useQueryClient();

  const { isRosteringEnabled } = useOrganization();

  const { teachers } = useSchoolCapabilities();

  const onSuccessAddTeachers = (): void => {
    const queryKey = getSchoolTeachersQueryKey({
      organizationId,
      isPlatformEnabled,
    });

    queryClient.refetchQueries({ queryKey });

    snack.success(t('teachers.resendInvitation.success'));
  };

  const onResendInvitationError = (error: GlowApiError): void => {
    snack.error(t('teachers.resendInvitation.error'));
  };

  const { isResendingInvitation, onResendInvitation } = useResendInvitation({
    onSuccess: onSuccessAddTeachers,
    onError: onResendInvitationError,
  });

  const isInvitation = teacher?.isSchoolInvitation || teacher?.isInvitation;

  const items: CreateItem[] = useMemo(() => {
    const options: CreateItem[] = [];

    if (teachers.canDelete(teacher)) {
      options.push({
        text: t('manager.table.deleteTeacher'),
        icon: 'DeleteIcon',
        state: isDeleteOptionDisabled ? 'disabled' : 'active',
        onSelectItem: () => onDeleteTeacher(teacher),
      });
    }

    if (isInvitation) {
      options.push({
        text: t('teachers.resendInvitation'),
        icon: 'MailIcon',
        state: isResendingInvitation ? 'disabled' : 'active',
        onSelectItem: () => onResendInvitation(teacher),
      });
    }

    if (!isUpdatePeriod && !isInvitation) {
      options.push({
        text: t('manager.table.editClassrooms'),
        icon: 'EditIcon',
        state: 'active',
        onSelectItem: () => onEditClassrooms(teacher),
      });
    }

    if (isRole.Admin) {
      options.push({
        icon: 'CopyIcon',
        onSelectItem() {
          copy(teacher?.id || '');
          snack.success(t('manager.table.copiedId'));
        },
        state: 'active',
        text: t('manager.table.copyId'),
      });
    }

    return options;
  }, [
    isDeleteOptionDisabled,
    isInvitation,
    isResendingInvitation,
    onDeleteTeacher,
    onEditClassrooms,
    onResendInvitation,
    t,
    teacher,
  ]);

  if (items.length === 0) {
    return null;
  }

  return (
    <ActionsDropdown
      dropdownId="school-teacher-options-button"
      items={items}
      buttonDataTestId="school-teacher-options-button"
    />
  );
}

export { ActionsCell };
